// src/components/Onboarding/WebGlGradientCanvas.js
// Lightweight wrapper around the Stripe-like WebGL gradient demo, adapted to fit the host element size.
// It renders only the animated background on a canvas; no text or extra UI.

import React, { useEffect, useRef } from 'react';

// Utilities
function normalizeColor(hexCode) {
  return [((hexCode >> 16) & 255) / 255, ((hexCode >> 8) & 255) / 255, (hexCode & 255) / 255];
}

// Mini WebGL helper (trimmed to only what's used)
class MiniGl {
  constructor(canvas, width, height, debug = false) {
    this.canvas = canvas;
    this.gl = this.canvas.getContext('webgl', { antialias: true });
    this.meshes = [];
    const context = this.gl;
    width && height && this.setSize(width, height);

    const mini = this;
    Object.defineProperties(this, {
      Material: {
        enumerable: false,
        value: class {
          constructor(vertexShaders, fragments, uniforms = {}) {
            const material = this;
            function getShaderByType(type, source) {
              const shader = context.createShader(type);
              context.shaderSource(shader, source);
              context.compileShader(shader);
              return shader;
            }
            function getUniformDecl(uniforms, type) {
              return Object.entries(uniforms).map(([name, u]) => u.getDeclaration(name, type)).join('\n');
            }
            material.uniforms = uniforms;
            material.uniformInstances = [];
            const prefix = 'precision highp float;\n';
            material.vertexSource = `\n${prefix}\nattribute vec4 position;\nattribute vec2 uv;\nattribute vec2 uvNorm;\n${getUniformDecl(mini.commonUniforms, 'vertex')}\n${getUniformDecl(uniforms, 'vertex')}\n${vertexShaders}`;
            material.Source = `\n${prefix}\n${getUniformDecl(mini.commonUniforms, 'fragment')}\n${getUniformDecl(uniforms, 'fragment')}\n${fragments}`;
            material.vertexShader = getShaderByType(context.VERTEX_SHADER, material.vertexSource);
            material.fragmentShader = getShaderByType(context.FRAGMENT_SHADER, material.Source);
            material.program = context.createProgram();
            context.attachShader(material.program, material.vertexShader);
            context.attachShader(material.program, material.fragmentShader);
            context.linkProgram(material.program);
            context.useProgram(material.program);
            material.attachUniforms(undefined, mini.commonUniforms);
            material.attachUniforms(undefined, material.uniforms);
          }
          attachUniforms(name, uniforms) {
            const material = this;
            if (typeof name === 'undefined') {
              Object.entries(uniforms).forEach(([n, u]) => material.attachUniforms(n, u));
            } else if (uniforms.type === 'array') {
              uniforms.value.forEach((u, i) => material.attachUniforms(`${name}[${i}]`, u));
            } else if (uniforms.type === 'struct') {
              Object.entries(uniforms.value).forEach(([n, u]) => material.attachUniforms(`${name}.${n}`, u));
            } else {
              material.uniformInstances.push({ uniform: uniforms, location: context.getUniformLocation(material.program, name) });
            }
          }
        },
      },
      Uniform: {
        enumerable: false,
        value: class {
          constructor(e) {
            this.type = 'float';
            Object.assign(this, e);
            this.typeFn = { float: '1f', int: '1i', vec2: '2fv', vec3: '3fv', vec4: '4fv', mat4: 'Matrix4fv' }[this.type] || '1f';
            this.update();
          }
          update(loc) {
            if (typeof this.value === 'undefined' || !loc) return;
            const isMatrix = this.typeFn.startsWith('Matrix');
            context[`uniform${this.typeFn}`](loc, isMatrix ? this.transpose : this.value, isMatrix ? this.value : null);
          }
          getDeclaration(name, type, length) {
            if (this.excludeFrom === type) return '';
            if (this.type === 'array') {
              return this.value[0].getDeclaration(name, type, this.value.length) + `\nconst int ${name}_length = ${this.value.length};`;
            }
            if (this.type === 'struct') {
              const nameNo = name.replace('u_', '');
              const structName = nameNo.charAt(0).toUpperCase() + nameNo.slice(1);
              return `uniform struct ${structName}{\n` +
                Object.entries(this.value).map(([n, u]) => u.getDeclaration(n, type).replace(/^uniform/, '')).join('') +
                `\n} ${name}${length > 0 ? `[${length}]` : ''};`;
            }
            return `uniform ${this.type} ${name}${length > 0 ? `[${length}]` : ''};`;
          }
        },
      },
      PlaneGeometry: {
        enumerable: false,
        value: class {
          constructor() {
            this.attributes = {
              position: new mini.Attribute({ target: context.ARRAY_BUFFER, size: 3 }),
              uv: new mini.Attribute({ target: context.ARRAY_BUFFER, size: 2 }),
              uvNorm: new mini.Attribute({ target: context.ARRAY_BUFFER, size: 2 }),
              index: new mini.Attribute({ target: context.ELEMENT_ARRAY_BUFFER, size: 3, type: context.UNSIGNED_SHORT }),
            };
            this.setTopology(1, 1);
            this.setSize(1, 1);
          }
          setTopology(x = 1, y = 1) {
            const g = this;
            g.xSegCount = x; g.ySegCount = y; g.vertexCount = (x + 1) * (y + 1); g.quadCount = x * y * 2;
            g.attributes.uv.values = new Float32Array(2 * g.vertexCount);
            g.attributes.uvNorm.values = new Float32Array(2 * g.vertexCount);
            g.attributes.index.values = new Uint16Array(3 * g.quadCount);
            for (let j = 0; j <= y; j++) {
              for (let i = 0; i <= x; i++) {
                const idx = j * (x + 1) + i;
                g.attributes.uv.values[2 * idx] = i / x;
                g.attributes.uv.values[2 * idx + 1] = 1 - j / y;
                g.attributes.uvNorm.values[2 * idx] = (i / x) * 2 - 1;
                g.attributes.uvNorm.values[2 * idx + 1] = 1 - (j / y) * 2;
                if (i < x && j < y) {
                  const q = j * x + i;
                  g.attributes.index.values[6 * q] = idx;
                  g.attributes.index.values[6 * q + 1] = idx + 1 + x;
                  g.attributes.index.values[6 * q + 2] = idx + 1;
                  g.attributes.index.values[6 * q + 3] = idx + 1;
                  g.attributes.index.values[6 * q + 4] = idx + 1 + x;
                  g.attributes.index.values[6 * q + 5] = idx + 2 + x;
                }
              }
            }
            g.attributes.uv.update();
            g.attributes.uvNorm.update();
            g.attributes.index.update();
          }
          setSize(width = 1, height = 1) {
            const g = this;
            g.width = width; g.height = height;
            if (!g.attributes.position.values || g.attributes.position.values.length !== 3 * g.vertexCount) {
              g.attributes.position.values = new Float32Array(3 * g.vertexCount);
            }
            const o = width / -2; const r = height / -2;
            const segW = width / g.xSegCount; const segH = height / g.ySegCount;
            for (let yIndex = 0; yIndex <= g.ySegCount; yIndex++) {
              const t = r + yIndex * segH;
              for (let xIndex = 0; xIndex <= g.xSegCount; xIndex++) {
                const px = o + xIndex * segW; const l = yIndex * (g.xSegCount + 1) + xIndex;
                g.attributes.position.values[3 * l + 0] = px;
                g.attributes.position.values[3 * l + 1] = -t;
              }
            }
            g.attributes.position.update();
          }
        },
      },
      Mesh: {
        enumerable: false,
        value: class {
          constructor(geometry, material) {
            this.geometry = geometry; this.material = material; this.wireframe = false; this.attributeInstances = [];
            Object.entries(this.geometry.attributes).forEach(([name, attr]) => {
              this.attributeInstances.push({ attribute: attr, location: attr.attach(name, material.program) });
            });
            mini.meshes.push(this);
          }
          draw() {
            context.useProgram(this.material.program);
            this.material.uniformInstances.forEach(({ uniform, location }) => uniform.update(location));
            this.attributeInstances.forEach(({ attribute, location }) => attribute.use(location));
            context.drawElements(this.wireframe ? context.LINES : context.TRIANGLES, this.geometry.attributes.index.values.length, context.UNSIGNED_SHORT, 0);
          }
          remove() { mini.meshes = mini.meshes.filter(m => m !== this); }
        },
      },
      Attribute: {
        enumerable: false,
        value: class {
          constructor(e) { this.type = context.FLOAT; this.normalized = false; this.buffer = context.createBuffer(); Object.assign(this, e); this.update(); }
          update() { if (this.values !== undefined) { context.bindBuffer(this.target, this.buffer); context.bufferData(this.target, this.values, context.STATIC_DRAW); } }
          attach(name, program) { const loc = context.getAttribLocation(program, name); if (this.target === context.ARRAY_BUFFER) { context.enableVertexAttribArray(loc); context.vertexAttribPointer(loc, this.size, this.type, this.normalized, 0, 0); } return loc; }
          use(loc) { context.bindBuffer(this.target, this.buffer); if (this.target === context.ARRAY_BUFFER) { context.enableVertexAttribArray(loc); context.vertexAttribPointer(loc, this.size, this.type, this.normalized, 0, 0); } }
        },
      },
    });

    // Now that classes are defined, we can initialize common uniforms
    const identity = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
    this.commonUniforms = {
      projectionMatrix: new this.Uniform({ type: 'mat4', value: identity }),
      modelViewMatrix: new this.Uniform({ type: 'mat4', value: identity }),
      resolution: new this.Uniform({ type: 'vec2', value: [1, 1] }),
      aspectRatio: new this.Uniform({ type: 'float', value: 1 }),
    };
  }
  setSize(w = 640, h = 480) {
    this.width = w; this.height = h; this.canvas.width = w; this.canvas.height = h; this.gl.viewport(0, 0, w, h);
    this.commonUniforms.resolution.value = [w, h]; this.commonUniforms.aspectRatio.value = w / h;
  }
  setOrthographicCamera() {
    this.commonUniforms.projectionMatrix.value = [
      2 / this.width, 0, 0, 0,
      0, 2 / this.height, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ];
  }
  render() { this.gl.clearColor(0, 0, 0, 0); this.gl.clearDepth(1); this.meshes.forEach(m => m.draw()); }
}

// Shader sources (trimmed)
const NOISE = `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);} 
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
float snoise(vec3 v){const vec2 C=vec2(1.0/6.0,1.0/3.0);const vec4 D=vec4(0.0,0.5,1.0,2.0);vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.0-g;vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;i=mod289(i);vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;vec4 j=p-49.0*floor(p*ns.z*ns.z);vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.0*x_);vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.0-abs(x)-abs(y);vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);vec4 s0=floor(b0)*2.0+1.0;vec4 s1=floor(b1)*2.0+1.0;vec4 sh=-step(h,vec4(0.0));vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);m=m*m;return 42.0*dot(m*m, vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));}`;

const BLEND = `
vec3 blendNormal(vec3 base, vec3 blend){return blend;} 
vec3 blendNormal(vec3 base, vec3 blend, float opacity){return (blendNormal(base, blend)*opacity + base*(1.0-opacity));}`;

const VERT_SRC = `
varying vec3 v_color; 
void main(){
  float time = u_time * u_global.noiseSpeed; 
  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;
  float tilt = resolution.y/2.0 * uvNorm.y; 
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline; 
  float offset = resolution.x/2.0 * u_vertDeform.incline * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);
  float noise = snoise(vec3(noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow, noiseCoord.y * u_vertDeform.noiseFreq.y, time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed)) * u_vertDeform.noiseAmp; 
  noise *= 1.0 - pow(abs(uvNorm.y), 2.0); noise = max(0.0, noise);
  vec3 pos = vec3(position.x, position.y + tilt + incline + noise - offset, position.z);
  if (u_active_colors[0] == 1.) { v_color = u_baseColor; }
  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.) {
      WaveLayers layer = u_waveLayers[i];
      float noise = smoothstep(layer.noiseFloor, layer.noiseCeil, snoise(vec3(noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow, noiseCoord.y * layer.noiseFreq.y, time * layer.noiseSpeed + layer.noiseSeed)) / 2.0 + 0.5);
      v_color = blendNormal(v_color, layer.color, pow(noise, 4.));
    }
  }
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}`;

const FRAG_SRC = `
varying vec3 v_color; 
void main(){ gl_FragColor = vec4(v_color, 1.0); }
`;

class Gradient {
  constructor(colorShiftSpeed = 3000) {
    this.el = undefined; this.cssVarRetries = 0; this.maxCssVarRetries = 50;
    this.angle = 0; this.isLoadedClass = false; this.width = 0; this.height = 0;
    this.mesh = undefined; this.material = undefined; this.geometry = undefined; this.minigl = undefined;
    this.amp = 320; this.seed = 5; 
    // Make frequencies configurable based on color shift speed (lower = faster)
    const speedFactor = 3000 / colorShiftSpeed; // Normalize to default 3000ms
    this.freqX = 14e-5 * speedFactor; 
    this.freqY = 29e-5 * speedFactor;
    this.activeColors = [1,1,1,1]; this.last = 0; this.t = 1253106;
    this.conf = { density: [.06, .16], playing: true };
  }
  initGradient(el) { this.el = el; this.connect(); return this; }
  async connect() {
    this.minigl = new MiniGl(this.el, null, null, false);
    requestAnimationFrame(() => {
      if (this.el) {
        this.computedCanvasStyle = getComputedStyle(this.el);
        this.waitForCssVars();
      }
    });
  }
  setSizeFromElement() {
    const rect = this.el.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    this.width = w; this.height = h;
    this.minigl.setSize(w, h);
    this.minigl.setOrthographicCamera();
    this.xSegCount = Math.ceil(w * this.conf.density[0]);
    this.ySegCount = Math.ceil(h * this.conf.density[1]);
    this.mesh.geometry.setTopology(this.xSegCount, this.ySegCount);
    this.mesh.geometry.setSize(w, h);
  }
  waitForCssVars() {
    if (this.computedCanvasStyle && this.computedCanvasStyle.getPropertyValue('--gradient-color-1').indexOf('#') !== -1) {
      this.init();
    } else {
      this.cssVarRetries += 1;
      if (this.cssVarRetries > this.maxCssVarRetries) {
        // Fallback colors
        this.sectionColors = [0xff0000, 0xff00ff, 0x00ff00, 0x0000ff].map(normalizeColor);
        this.init();
        return;
      }
      requestAnimationFrame(() => this.waitForCssVars());
    }
  }
  initGradientColors() {
    this.sectionColors = ['--gradient-color-1', '--gradient-color-2', '--gradient-color-3', '--gradient-color-4']
      .map(name => {
        let hex = this.computedCanvasStyle.getPropertyValue(name).trim();
        if (hex && hex.length === 4) {
          const t = hex.substr(1).split('').map(c => c + c).join('');
          hex = `#${t}`;
        }
        return hex ? `0x${hex.substr(1)}` : null;
      })
      .filter(Boolean)
      .map(normalizeColor);
  }
  initMaterial() {
    this.uniforms = {
      u_time: new this.minigl.Uniform({ value: 0 }),
      u_active_colors: new this.minigl.Uniform({ value: this.activeColors, type: 'vec4' }),
      u_global: new this.minigl.Uniform({ value: { noiseFreq: new this.minigl.Uniform({ value: [this.freqX, this.freqY], type: 'vec2' }), noiseSpeed: new this.minigl.Uniform({ value: 5e-6 }) }, type: 'struct' }),
      u_vertDeform: new this.minigl.Uniform({ value: { incline: new this.minigl.Uniform({ value: Math.sin(this.angle) / Math.cos(this.angle) }), offsetTop: new this.minigl.Uniform({ value: -0.5 }), offsetBottom: new this.minigl.Uniform({ value: -0.5 }), noiseFreq: new this.minigl.Uniform({ value: [3,4], type: 'vec2' }), noiseAmp: new this.minigl.Uniform({ value: this.amp }), noiseSpeed: new this.minigl.Uniform({ value: 10 }), noiseFlow: new this.minigl.Uniform({ value: 3 }), noiseSeed: new this.minigl.Uniform({ value: this.seed }) }, type: 'struct', excludeFrom: 'fragment' }),
      u_baseColor: new this.minigl.Uniform({ value: this.sectionColors?.[0] || [1,1,1], type: 'vec3', excludeFrom: 'fragment' }),
      u_waveLayers: new this.minigl.Uniform({ value: [], excludeFrom: 'fragment', type: 'array' }),
    };
    for (let i = 1; i < (this.sectionColors?.length || 1); i++) {
      this.uniforms.u_waveLayers.value.push(new this.minigl.Uniform({
        value: {
          color: new this.minigl.Uniform({ value: this.sectionColors[i], type: 'vec3' }),
          noiseFreq: new this.minigl.Uniform({ value: [2 + i / this.sectionColors.length, 3 + i / this.sectionColors.length], type: 'vec2' }),
          noiseSpeed: new this.minigl.Uniform({ value: 11 + 0.3 * i }),
          noiseFlow: new this.minigl.Uniform({ value: 6.5 + 0.3 * i }),
          noiseSeed: new this.minigl.Uniform({ value: this.seed + 10 * i }),
          noiseFloor: new this.minigl.Uniform({ value: 0.1 }),
          noiseCeil: new this.minigl.Uniform({ value: 0.63 + 0.07 * i }),
        },
        type: 'struct',
      }));
    }
    this.vertexShader = [NOISE, BLEND, VERT_SRC].join('\n');
    return new this.minigl.Material(this.vertexShader, FRAG_SRC, this.uniforms);
  }
  initMesh() {
    this.material = this.initMaterial();
    this.geometry = new this.minigl.PlaneGeometry();
    this.mesh = new this.minigl.Mesh(this.geometry, this.material);
  }
  resize = () => {
    if (!this.el) return;
    this.setSizeFromElement();
  };
  animate = (ts) => {
    if (!this.conf.playing) return;
    if (!this.last) this.last = ts;
    this.t += Math.min(ts - this.last, 1000 / 15);
    this.last = ts;
    this.mesh.material.uniforms.u_time.value = this.t;
    this.minigl.render();
    requestAnimationFrame(this.animate);
  };
  init() {
    this.initGradientColors();
    this.initMesh();
    this.resize();
    requestAnimationFrame(this.animate);
    window.addEventListener('resize', this.resize);
  }
  disconnect() {
    window.removeEventListener('resize', this.resize);
  }
}

export default function WebGlGradientCanvas({ className = '', style = {}, colorShiftSpeed = 3000 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const grad = new Gradient(colorShiftSpeed);
    grad.initGradient(el);
    return () => grad.disconnect();
  }, [colorShiftSpeed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={style}
      aria-hidden="true"
    />
  );
}
